// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Developer-only debug helper: fills the active round with gross scores for every
// hole and every player, plus a few representative events (water, three-putts) and
// mystery-prize winners when the round has Mystery configured. Not a security
// boundary — gating is in the UI; this just drives the existing BFF WebSocket API.

import { useCallback, useState } from 'react';

import { getSharedBffWebSocket } from '@/services/bffWebSocket';

type UseScoreAllHolesParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  roundId: string | null | undefined;
};

export type ScoreAllHolesSummary = {
  holes: number;
  players: number;
  scores: number;
  events: number;
  mystery: number;
};

export type ScoreAllHolesPlayerScore = {
  playerId: string;
  playerName: string;
  strokes: number;
  events: string[];
};

export type ScoreAllHolesHole = {
  hole: number;
  par: number;
  scores: ScoreAllHolesPlayerScore[];
};

export type ScoreAllHolesMystery = {
  hole: number;
  playerName: string;
  subgameId: string;
};

export type ScoreAllHolesResult = {
  summary: ScoreAllHolesSummary;
  holes: ScoreAllHolesHole[];
  mystery: ScoreAllHolesMystery[];
};

type RoundPlayer = { id: string; name: string };
type RoundHole = { number: number; par: number };
type SubgameAssignment = { playerId: string; holeNumber: number; subgameId: string };

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Random gross score for a hole, around its par (birdie .. triple bogey).
const randomStrokesForPar = (par: number): number => {
  const offset = Math.floor(Math.random() * 5) - 1; // -1 .. +3
  return Math.max(1, par + offset);
};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

// Some round endpoints return a bare array, others wrap it (e.g. `{ players: [...] }`).
const arrayFrom = (value: unknown, ...keys: string[]): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    for (const key of keys) {
      const nested = (value as Record<string, unknown>)[key];
      if (Array.isArray(nested)) return nested;
    }
  }
  return [];
};

const readString = (record: Record<string, unknown>, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const readNumber = (record: Record<string, unknown>, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const parsePlayers = (data: unknown): RoundPlayer[] =>
  arrayFrom(data, 'players', 'Players')
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const record = entry as Record<string, unknown>;
      const id = readString(record, 'id', 'Id', 'playerId', 'PlayerId');
      if (!id) return null;
      const name = readString(record, 'name', 'Name', 'displayName', 'DisplayName') ?? id;
      return { id, name };
    })
    .filter((p): p is RoundPlayer => p !== null);

const parseHoles = (payload: unknown): RoundHole[] => {
  if (!payload || typeof payload !== 'object') return [];
  const record = payload as Record<string, unknown>;
  const rawHoles = (record.holes ?? record.Holes) as unknown;
  const holes = asArray(rawHoles)
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const r = entry as Record<string, unknown>;
      const number = readNumber(r, 'number', 'Number', 'holeNumber', 'HoleNumber');
      if (number === null) return null;
      const par = readNumber(r, 'par', 'Par') ?? 4;
      return { number, par };
    })
    .filter((h): h is RoundHole => h !== null);
  return holes;
};

const parseSubgames = (data: unknown): SubgameAssignment[] =>
  asArray(data)
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const r = entry as Record<string, unknown>;
      const playerId = readString(r, 'playerId', 'PlayerId');
      const subgameId = readString(r, 'subgameId', 'SubgameId');
      const holeNumber = readNumber(r, 'holeNumber', 'HoleNumber');
      if (!playerId || !subgameId || holeNumber === null) return null;
      return { playerId, subgameId, holeNumber };
    })
    .filter((s): s is SubgameAssignment => s !== null);

type GameDef = { id: string; name: string; defaultWeightPercent: number };

const parseGameDefs = (data: unknown): GameDef[] =>
  asArray(data)
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const r = entry as Record<string, unknown>;
      const id = readString(r, 'id', 'Id');
      const name = readString(r, 'name', 'Name');
      if (!id || !name) return null;
      const weight = readNumber(r, 'defaultWeightPercent', 'DefaultWeightPercent') ?? 100;
      return { id, name, defaultWeightPercent: weight };
    })
    .filter((g): g is GameDef => g !== null);

// The scores fixture produced by tools/game-calculator/fetch-round-scores.py.
export type ImportHazardCounts = {
  bunker?: number;
  water?: number;
  threePutt?: number;
  penalty?: number;
  chipIn?: number;
};
export type ImportFixture = {
  players?: Array<{ id?: string; name?: string; playingHandicap?: number }>;
  rounds?: Array<{
    holes?: Array<{ number?: number; par?: number }>;
    games?: Array<{ gameId?: string; gameName?: string; weightPercent?: number }>;
  }>;
  scores?: Array<{
    playerId?: string;
    holeNumber?: number;
    grossStrokes?: number | null;
    pickup?: boolean;
    hazardCounts?: ImportHazardCounts;
  }>;
};

const importGenerateId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
    if (uuid) return uuid;
  }
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const HAZARD_EVENTS: Array<[keyof ImportHazardCounts, string]> = [
  ['water', 'water'],
  ['bunker', 'bunker'],
  ['threePutt', 'threePutt'],
  ['penalty', 'penalty'],
  ['chipIn', 'chipIn'],
];

export const useScoreAllHoles = ({ apiBaseUrl, token, roundId }: UseScoreAllHolesParams) => {
  const [isRunning, setIsRunning] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scoreAllHoles = useCallback(async (maxHoles?: number): Promise<ScoreAllHolesResult> => {
    if (!roundId) throw new Error('No active round.');
    if (!token) throw new Error('Not authenticated.');

    setIsRunning(true);
    setError(null);
    setStatus('Loading round…');

    const authHeaders = { Authorization: `Bearer ${token}` };
    try {
      const [playersRes, roundRes, subgamesRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, { headers: authHeaders }),
      ]);
      if (!playersRes.ok) throw new Error(`Failed to load players (${playersRes.status}).`);
      if (!roundRes.ok) throw new Error(`Failed to load round (${roundRes.status}).`);

      const players = parsePlayers(await playersRes.json());
      if (players.length === 0) throw new Error('Round has no players.');

      let holes = parseHoles(await roundRes.json());
      if (holes.length === 0) {
        // Fall back to a standard 18-hole, par-4 layout if the round payload lacks holes.
        holes = Array.from({ length: 18 }, (_, i) => ({ number: i + 1, par: 4 }));
      }
      holes.sort((a, b) => a.number - b.number);
      if (maxHoles && maxHoles > 0 && maxHoles < holes.length) {
        holes = holes.slice(0, maxHoles);
      }

      const subgames = subgamesRes.ok ? parseSubgames(await subgamesRes.json()) : [];
      const playerName = (id: string): string => players.find((p) => p.id === id)?.name ?? id;

      const ws = getSharedBffWebSocket(apiBaseUrl);
      // The shared socket is normally configured by the active screen; make sure it is
      // authenticated and joined to this round before we start sending.
      ws.setAuthToken(token);
      ws.setSubscriptionContext({ roundId });

      const holeRows: ScoreAllHolesHole[] = [];
      let scoreCount = 0;
      let eventCount = 0;
      console.info('[score-all-holes] starting', { roundId, players: players.length, holes: holes.length, subgames: subgames.length });

      for (let h = 0; h < holes.length; h += 1) {
        const hole = holes[h];
        setStatus(`Scoring hole ${hole.number}/${holes.length}…`);
        const rowScores: ScoreAllHolesPlayerScore[] = players.map((player) => ({
          playerId: player.id,
          playerName: player.name,
          strokes: randomStrokesForPar(hole.par),
          events: [],
        }));

        for (const row of rowScores) {
          ws.send({
            type: 'score.update',
            roundId,
            playerId: row.playerId,
            hole: hole.number,
            strokes: row.strokes,
            pickup: false,
            par: hole.par,
            timestamp: new Date().toISOString(),
          });
          scoreCount += 1;
          await delay(80);
        }

        // A few representative hole events so event-driven games (e.g. water, putts) have data.
        if (hole.number % 3 === 0 && rowScores[0]) {
          ws.send({
            type: 'hole.event',
            roundId,
            playerId: rowScores[0].playerId,
            holeNumber: hole.number,
            eventType: 'water',
            eventValue: null,
            eventDelta: 1,
            timestamp: new Date().toISOString(),
          });
          rowScores[0].events.push('water');
          eventCount += 1;
          await delay(40);
        }
        if (hole.number % 4 === 0 && rowScores[1]) {
          ws.send({
            type: 'hole.event',
            roundId,
            playerId: rowScores[1].playerId,
            holeNumber: hole.number,
            eventType: 'threePutt',
            eventValue: null,
            eventDelta: 1,
            timestamp: new Date().toISOString(),
          });
          rowScores[1].events.push('threePutt');
          eventCount += 1;
          await delay(40);
        }

        holeRows.push({ hole: hole.number, par: hole.par, scores: rowScores });
      }

      // Mystery prizes: only when the round has Mystery assignments configured,
      // and only for holes we actually scored in this run.
      const scoredHoleNumbers = new Set(holes.map((hole) => hole.number));
      const mysteryAssignments = subgames.filter((assignment) => scoredHoleNumbers.has(assignment.holeNumber));
      const mysteryRows: ScoreAllHolesMystery[] = [];
      if (mysteryAssignments.length > 0) {
        setStatus('Recording mystery winners…');
        for (const assignment of mysteryAssignments) {
          ws.send({
            type: 'hole.event',
            roundId,
            playerId: assignment.playerId,
            holeNumber: assignment.holeNumber,
            eventType: 'mysteryWinner',
            eventValue: assignment.subgameId,
            eventDelta: 1,
            timestamp: new Date().toISOString(),
          });
          mysteryRows.push({ hole: assignment.holeNumber, playerName: playerName(assignment.playerId), subgameId: assignment.subgameId });
          await delay(40);
        }
      }

      setStatus(null);
      const result: ScoreAllHolesResult = {
        summary: { holes: holes.length, players: players.length, scores: scoreCount, events: eventCount, mystery: mysteryRows.length },
        holes: holeRows,
        mystery: mysteryRows,
      };
      console.info('[score-all-holes] done', result.summary);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to score all holes.';
      setError(message);
      setStatus(null);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setIsRunning(false);
    }
  }, [apiBaseUrl, token, roundId]);

  // Import a fetch-round-scores.py export and replay it onto the active round:
  // match/create the file's players, configure the round's games, and send the file's
  // real scores/events (limited to maxHoles). Player ids are remapped by name.
  const importAndScoreAllHoles = useCallback(async (fixture: ImportFixture, maxHoles?: number, clean?: boolean): Promise<ScoreAllHolesResult> => {
    if (!roundId) throw new Error('No active round.');
    if (!token) throw new Error('Not authenticated.');

    setIsRunning(true);
    setError(null);
    setStatus('Reading round…');

    const authHeaders = { Authorization: `Bearer ${token}` };
    const norm = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

    try {
      // Optional: wipe existing scores/events/members first (developer-only reset endpoint).
      if (clean) {
        setStatus('Clearing existing players & scores…');
        const resetRes = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/reset`, {
          method: 'POST',
          headers: authHeaders,
        });
        if (!resetRes.ok) {
          throw new Error(`Failed to reset round (${resetRes.status}).`);
        }
      }

      const [roundRes, roundPlayersRes, platformRes, gameDefsRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/players/platform`, { headers: authHeaders }),
        fetch(`${apiBaseUrl}/api/game-definitions`, { headers: authHeaders }),
      ]);
      if (!roundRes.ok) throw new Error(`Failed to load round (${roundRes.status}).`);
      const roundData = (await roundRes.json()) as Record<string, unknown>;
      const tourId = readString(roundData, 'tourId', 'TourId');
      if (!tourId) throw new Error('Round has no tour to add players to.');

      const roundPlayers = roundPlayersRes.ok ? parsePlayers(await roundPlayersRes.json()) : [];
      const platformPlayers = platformRes.ok ? parsePlayers(await platformRes.json()) : [];
      const gameDefs = gameDefsRes.ok ? parseGameDefs(await gameDefsRes.json()) : [];

      // Tour members include temporary players (the platform list does not), so match
      // against them too — otherwise every clean import re-creates the same players.
      type ImportTourMember = { playerId: string; name: string; isTemporary: boolean };
      let tourMembers: ImportTourMember[] = [];
      const membersRes = await fetch(`${apiBaseUrl}/api/tours/${tourId}/members`, { headers: authHeaders }).catch(() => null);
      if (membersRes?.ok) {
        tourMembers = asArray(await membersRes.json())
          .map((entry) => {
            if (!entry || typeof entry !== 'object') return null;
            const r = entry as Record<string, unknown>;
            const playerId = readString(r, 'playerId', 'PlayerId');
            if (!playerId) return null;
            const name = readString(r, 'name', 'Name') ?? playerId;
            const isTemporary = r.isTemporary === true || r.IsTemporary === true;
            return { playerId, name, isTemporary };
          })
          .filter((m): m is ImportTourMember => m !== null);
      }

      const fixtureRound = (fixture.rounds ?? [])[0] ?? {};
      const fixturePlayers = (fixture.players ?? []).filter((p) => p && p.id && p.name) as Array<{ id: string; name: string; playingHandicap?: number }>;
      if (fixturePlayers.length === 0) throw new Error('Import file has no players.');

      let holes = (fixtureRound.holes ?? [])
        .map((h) => ({ number: h.number ?? 0, par: h.par ?? 4 }))
        .filter((h) => h.number > 0)
        .sort((a, b) => a.number - b.number);
      if (holes.length === 0) throw new Error('Import file has no holes.');
      if (maxHoles && maxHoles > 0 && maxHoles < holes.length) {
        holes = holes.slice(0, maxHoles);
      }

      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(token);
      ws.setSubscriptionContext({ roundId, tourId });

      // Send a message with a requestId and resolve on the first matching ack (or timeout).
      const sendAndAwaitAck = (message: Record<string, unknown>, ackTypes: string[], timeoutMs = 12000): Promise<Record<string, unknown>> =>
        new Promise((resolve, reject) => {
          const requestId = importGenerateId();
          let settled = false;
          const unsubscribe = ws.onMessage((incoming: unknown) => {
            if (settled || !incoming || typeof incoming !== 'object') return;
            const m = incoming as Record<string, unknown>;
            const type = typeof m.type === 'string' ? m.type : '';
            if (!ackTypes.includes(type)) return;
            const rid = typeof m.requestId === 'string' ? m.requestId : null;
            if (rid && rid !== requestId) return; // if the ack carries a requestId it must match ours
            settled = true;
            unsubscribe();
            clearTimeout(timer);
            resolve(m);
          });
          const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            unsubscribe();
            reject(new Error(`Timed out waiting for ${ackTypes[0]}.`));
          }, timeoutMs);
          ws.send({ ...message, requestId });
        });

      // 1) Players: match by name (round -> tour member -> platform); create only as a last resort.
      setStatus('Setting up players…');
      const roundByName = new Map(roundPlayers.map((p) => [norm(p.name), p]));
      const tourByName = new Map(tourMembers.map((m) => [norm(m.name), m]));
      const platformByName = new Map(platformPlayers.map((p) => [norm(p.name), p]));
      const localIdByFixtureId = new Map<string, string>();
      const nameByFixtureId = new Map<string, string>();
      const matchedTourPlayerIds = new Set<string>();
      const scpByLocalId: Array<{ id: string; scp: string }> = [];

      for (const fp of fixturePlayers) {
        nameByFixtureId.set(fp.id, fp.name);
        const key = norm(fp.name);
        let localId = roundByName.get(key)?.id ?? null;
        if (!localId) {
          const tourMember = tourByName.get(key) ?? null;
          if (tourMember) {
            // Already a tour member (possibly temporary): just add to the round.
            setStatus(`Adding ${fp.name} to round…`);
            await sendAndAwaitAck({ type: 'round.member.add', roundId, playerId: tourMember.playerId }, ['round.member.added']).catch(() => null);
            localId = tourMember.playerId;
          } else {
            let platformId = platformByName.get(key)?.id ?? null;
            if (!platformId) {
              setStatus(`Creating player ${fp.name}…`);
              const created = await sendAndAwaitAck({ type: 'player.temporary.create', name: fp.name }, ['player.temporary.created', 'players.temporary.created']);
              platformId = readString(created, 'playerId', 'PlayerId');
              if (!platformId) throw new Error(`Failed to create player ${fp.name}.`);
            }
            setStatus(`Adding ${fp.name} to round…`);
            await sendAndAwaitAck({ type: 'tour.member.add', tourId, playerId: platformId }, ['tour.member.added']).catch(() => null);
            await sendAndAwaitAck({ type: 'round.member.add', roundId, playerId: platformId }, ['round.member.added']).catch(() => null);
            localId = platformId;
          }
        }
        matchedTourPlayerIds.add(localId);
        localIdByFixtureId.set(fp.id, localId);
        if (typeof fp.playingHandicap === 'number') {
          scpByLocalId.push({ id: localId, scp: String(fp.playingHandicap) });
        }
      }

      // Clean mode: prune temporary tour members that match no fixture player, so
      // repeated imports don't accumulate duplicate "Unknown player" rows. Members
      // with recorded scores in other rounds are rejected server-side and skipped.
      if (clean) {
        const staleMembers = tourMembers.filter((m) => m.isTemporary && !matchedTourPlayerIds.has(m.playerId));
        for (const member of staleMembers) {
          setStatus(`Removing stale player ${member.name}…`);
          await sendAndAwaitAck({ type: 'tour.member.remove', tourId, playerId: member.playerId }, ['tour.member.removed']).catch(() => null);
        }
      }

      // 2) Playing handicaps (SCP) for accurate handicap-based scoring.
      if (scpByLocalId.length > 0) {
        setStatus('Setting handicaps…');
        await fetch(`${apiBaseUrl}/api/rounds/${roundId}/players/shcp`, {
          method: 'PUT',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ players: scpByLocalId }),
        }).catch(() => null);
      }

      // 3) Configure the round's games (resolve fixture game name -> definition id).
      setStatus('Configuring games…');
      const defByName = new Map(gameDefs.map((g) => [norm(g.name), g]));
      const selectedGames: Array<{ id: string; weight: number }> = [];
      for (const g of fixtureRound.games ?? []) {
        const def = defByName.get(norm(g.gameName ?? g.gameId));
        if (!def) continue; // no matching game definition; skip
        if (selectedGames.some((s) => s.id === def.id)) continue;
        selectedGames.push({ id: def.id, weight: typeof g.weightPercent === 'number' ? g.weightPercent : def.defaultWeightPercent });
      }
      if (selectedGames.length > 0) {
        // Wait until the games are actually configured on the round: hole.scored events
        // carry the round's game definitions, so scores replayed before this lands are
        // never picked up by the games calculation.
        const gamesAck = await sendAndAwaitAck(
          {
            type: 'round.update',
            roundId,
            tourId,
            gameDefinitionIds: selectedGames.map((s) => s.id),
            gameWeights: selectedGames.map((s) => ({ gameDefinitionId: s.id, weightPercent: s.weight })),
          },
          ['round.updated', 'round.update.rejected'],
          20000
        ).catch(() => null);
        if (gamesAck && gamesAck.type === 'round.update.rejected') {
          throw new Error('Failed to configure games for the round.');
        }
        if (!gamesAck) {
          // No ack seen (broadcast may have been missed); give the backend a moment.
          await delay(1500);
        }
      }

      // 4) Replay scores + hazard/chip-in events for the selected holes.
      const holeSet = new Set(holes.map((h) => h.number));
      const scoresByHole = new Map<number, ImportFixture['scores']>();
      for (const s of fixture.scores ?? []) {
        if (s.holeNumber === undefined || !holeSet.has(s.holeNumber)) continue;
        const arr = scoresByHole.get(s.holeNumber) ?? [];
        arr!.push(s);
        scoresByHole.set(s.holeNumber, arr!);
      }

      const holeRows: ScoreAllHolesHole[] = [];
      let scoreCount = 0;
      let eventCount = 0;
      const nowIso = (): string => new Date().toISOString();

      for (const hole of holes) {
        setStatus(`Scoring hole ${hole.number}/${holes.length}…`);
        const rowScores: ScoreAllHolesPlayerScore[] = [];
        for (const s of scoresByHole.get(hole.number) ?? []) {
          const localId = s.playerId ? localIdByFixtureId.get(s.playerId) : undefined;
          if (!localId) continue;
          const strokes = typeof s.grossStrokes === 'number' ? s.grossStrokes : 0;
          ws.send({
            type: 'score.update',
            roundId,
            playerId: localId,
            hole: hole.number,
            strokes,
            pickup: s.pickup ?? false,
            par: hole.par,
            timestamp: nowIso(),
          });
          scoreCount += 1;
          await delay(60);

          const events: string[] = [];
          for (const [field, eventType] of HAZARD_EVENTS) {
            const count = s.hazardCounts?.[field] ?? 0;
            for (let i = 0; i < count; i += 1) {
              ws.send({
                type: 'hole.event',
                roundId,
                playerId: localId,
                holeNumber: hole.number,
                eventType,
                eventValue: null,
                eventDelta: 1,
                timestamp: nowIso(),
              });
              events.push(eventType);
              eventCount += 1;
              await delay(30);
            }
          }
          rowScores.push({
            playerId: localId,
            playerName: (s.playerId ? nameByFixtureId.get(s.playerId) : null) ?? localId,
            strokes,
            events,
          });
        }
        holeRows.push({ hole: hole.number, par: hole.par, scores: rowScores });
      }

      // 5) Force a full game recalculation so merits/game points cover every replayed
      // hole, even if some hole.scored events raced the games configuration.
      setStatus('Calculating games…');
      await fetch(`${apiBaseUrl}/api/rounds/${roundId}/recalculate`, {
        method: 'POST',
        headers: authHeaders,
      }).catch(() => null);

      setStatus(null);
      const result: ScoreAllHolesResult = {
        summary: { holes: holes.length, players: fixturePlayers.length, scores: scoreCount, events: eventCount, mystery: 0 },
        holes: holeRows,
        mystery: [],
      };
      console.info('[score-all-holes] import done', result.summary);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import round.';
      setError(message);
      setStatus(null);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setIsRunning(false);
    }
  }, [apiBaseUrl, token, roundId]);

  return { scoreAllHoles, importAndScoreAllHoles, isRunning, status, error };
};
